import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { getStoreColor } from "../../constants/plasticColors";
import { storeLabel } from "../../utils/storeLabel";
import type {
  IParcelReport,
  IParcelReportRow,
} from "../../store/useParcelReport";

dayjs.extend(isoWeek);

export type GroupBy = "day" | "week" | "month";

/** The carrier filter value that reports every carrier together. */
export const ALL_CARRIERS = "all";

const DAY_FORMAT = "YYYY-MM-DD";

export const isDay = (value?: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value ?? "");

/**
 * A day the report can use. While a year is typed into a date input, Chrome
 * already reports 0002, 0020 and 0202 as values; those are half-typed, not
 * choices.
 */
export const isReportDay = (value?: string) =>
  isDay(value) && Number((value ?? "").slice(0, 4)) >= 2000;

const amsterdamDayFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Amsterdam",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Today's calendar date in Amsterdam, as YYYY-MM-DD. The report's days are
 * Amsterdam days, and the people choosing ranges are not all in that time
 * zone, so "today" is never the browser's own date.
 */
export const amsterdamToday = () => {
  const parts = amsterdamDayFormat.formatToParts(new Date());
  const part = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};

/** The quick ranges. Plain calendar arithmetic on the Amsterdam date. */
export const rangePresets = () => {
  const today = dayjs(amsterdamToday());
  const day = (d: Dayjs) => d.format(DAY_FORMAT);
  const yesterday = today.subtract(1, "day");
  return [
    { label: "Today", dateFrom: day(today), dateTo: day(today) },
    { label: "Yesterday", dateFrom: day(yesterday), dateTo: day(yesterday) },
    {
      label: "Last 7 days",
      dateFrom: day(today.subtract(6, "day")),
      dateTo: day(today),
    },
    {
      label: "Last 30 days",
      dateFrom: day(today.subtract(29, "day")),
      dateTo: day(today),
    },
    {
      label: "This month",
      dateFrom: day(today.startOf("month")),
      dateTo: day(today),
    },
  ];
};

/** The last 14 days, today included. */
export const defaultRange = () => {
  const today = dayjs(amsterdamToday());
  return {
    dateFrom: today.subtract(13, "day").format(DAY_FORMAT),
    dateTo: today.format(DAY_FORMAT),
  };
};

const countFormat = new Intl.NumberFormat("nl-NL", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatCount = (value: number) => countFormat.format(value);

export interface IReportBucket {
  /** The day, the week's Monday (both YYYY-MM-DD) or the month (YYYY-MM). */
  key: string;
  /** The first and last day of the range this bucket covers; a first or last
   *  week or month can be partial. */
  first: string;
  last: string;
  /** Short x-axis label. */
  label: string;
  /** Tooltip heading, with the days of the range the bucket covers. */
  title: string;
}

export interface IReportBuckets {
  buckets: IReportBucket[];
  /** The index of each report day's bucket. */
  bucketOfDay: Map<string, number>;
}

const bucketKey = (day: string, groupBy: GroupBy) => {
  if (groupBy === "week") {
    return dayjs(day).startOf("isoWeek").format(DAY_FORMAT);
  }
  if (groupBy === "month") return day.slice(0, 7);
  return day;
};

/** "29 Sep - 5 Oct 2026", or one day when the span is one day. */
export const spanLabel = (first: string, last: string) => {
  if (first === last) return dayjs(first).format("D MMM YYYY");
  const sameYear = first.slice(0, 4) === last.slice(0, 4);
  return `${dayjs(first).format(sameYear ? "D MMM" : "D MMM YYYY")} - ${dayjs(
    last
  ).format("D MMM YYYY")}`;
};

/**
 * One bucket per day, ISO week (Monday first) or month of the report's days.
 * The days cover the whole range, so every bucket appears, empty ones too.
 * The first and last bucket can be partial; their tooltip says which days
 * they cover.
 */
export const buildBuckets = (
  days: string[],
  groupBy: GroupBy
): IReportBuckets => {
  const bucketOfDay = new Map<string, number>();
  if (!days.length) return { buckets: [], bucketOfDay };

  // Across New Year the same day or Monday can come round twice.
  const spansYears = days[0].slice(0, 4) !== days[days.length - 1].slice(0, 4);
  const shortFormat = spansYears ? "D MMM YYYY" : "D MMM";

  const groups: { key: string; first: string; last: string }[] = [];
  days.forEach((day) => {
    const key = bucketKey(day, groupBy);
    const current = groups[groups.length - 1];
    if (current?.key === key) {
      current.last = day;
    } else {
      groups.push({ key, first: day, last: day });
    }
    bucketOfDay.set(day, groups.length - 1);
  });

  const buckets = groups.map(({ key, first, last }) => {
    if (groupBy === "week") {
      return {
        key,
        first,
        last,
        // The first day it covers, which is the Monday unless the range
        // starts mid-week.
        label: dayjs(first).format(shortFormat),
        title: `Week ${dayjs(key).isoWeek()}: ${spanLabel(first, last)}`,
      };
    }
    if (groupBy === "month") {
      return {
        key,
        first,
        last,
        label: dayjs(`${key}-01`).format("MMM YYYY"),
        title: spanLabel(first, last),
      };
    }
    return {
      key,
      first,
      last,
      label: dayjs(key).format(shortFormat),
      title: dayjs(key).format("ddd D MMM YYYY"),
    };
  });

  return { buckets, bucketOfDay };
};

/** "Dropped off" and "Picked up" wear these wherever they appear. */
export const TYPE_COLORS = { dropoff: "#ff6e41", pickup: "#2a78d6" };

export interface IReportStore {
  locationId: string;
  label: string;
  color: string;
}

/**
 * The report's stores, sorted by label, each in its fixed dashboard colour
 * (STORE_COLOR_MAP). The colour is looked up by the cost place
 * (001_VLS_Ams_FS); a name would not do, since "Smaragdplein" contains MAR.
 * Also covers a row whose store is missing from `stores` (a deleted
 * location), so no parcel drops out of the per-store charts.
 */
export const reportStores = (report: IParcelReport | null): IReportStore[] => {
  if (!report) return [];
  const listed = report.stores ?? [];
  const ids = Array.from(
    new Set([
      ...listed.map((s) => String(s.locationId)),
      ...(report.rows ?? []).map((r) => String(r.locationId)),
    ])
  );

  const stores = ids
    .map((locationId) => {
      const store = listed.find((s) => String(s.locationId) === locationId);
      return {
        locationId,
        costPlace: store?.costPlace ?? "",
        label:
          storeLabel({ name: store?.name, cost_place: store?.costPlace }) ||
          "Unknown store",
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return stores.map(({ locationId, costPlace, label }) => ({
    locationId,
    label,
    color: getStoreColor(costPlace),
  }));
};

export interface IReportSeries {
  name: string;
  data: number[];
  color: string;
}

export interface IReportSeriesSet {
  byType: IReportSeries[];
  parcelsByStore: IReportSeries[];
  stampsByStore: IReportSeries[];
  firstDropsByStore: IReportSeries[];
}

/**
 * The daily rows summed into buckets. A store with nothing to show in a chart
 * is left out of that chart's legend; colours belong to the store, so the
 * others keep theirs.
 */
export const buildSeries = (
  rows: IParcelReportRow[],
  { buckets, bucketOfDay }: IReportBuckets,
  stores: IReportStore[]
): IReportSeriesSet => {
  const zeros = () => buckets.map(() => 0);
  const dropoffs = zeros();
  const pickups = zeros();
  const perStore = new Map(
    stores.map((s) => [
      s.locationId,
      { parcels: zeros(), stamps: zeros(), firstDrops: zeros() },
    ])
  );

  rows.forEach((row) => {
    const index = bucketOfDay.get(row.date);
    const store = perStore.get(String(row.locationId));
    if (index === undefined || !store) return;
    const dropped = Number(row.dropoffs) || 0;
    const picked = Number(row.pickups) || 0;
    dropoffs[index] += dropped;
    pickups[index] += picked;
    store.parcels[index] += dropped + picked;
    store.stamps[index] += Number(row.stamps) || 0;
    store.firstDrops[index] += Number(row.firstDrops) || 0;
  });

  const byStore = (field: "parcels" | "stamps" | "firstDrops") =>
    stores
      .map((s) => ({
        name: s.label,
        color: s.color,
        data: perStore.get(s.locationId)?.[field] ?? zeros(),
      }))
      .filter((s) => s.data.some((value) => value > 0));

  return {
    byType: [
      { name: "Dropped off", color: TYPE_COLORS.dropoff, data: dropoffs },
      { name: "Picked up", color: TYPE_COLORS.pickup, data: pickups },
    ],
    parcelsByStore: byStore("parcels"),
    stampsByStore: byStore("stamps"),
    firstDropsByStore: byStore("firstDrops"),
  };
};

/** A carrier name as a file name part: "Vinted Go" becomes "vinted_go". */
export const fileSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "carrier";
